import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatCircleDots, PaperPlaneRight, X, Plus, Trash, ClockCounterClockwise } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  sendChatMessage,
  listChatSessions,
  getChatHistory,
  deleteChatSession
} from '../services/api';

const LABELS = {
  en: {
    title: 'Carex Assistant',
    subtitle: 'Ask me anything about the system',
    placeholder: 'Type your question…',
    send: 'Send',
    new: 'New chat',
    empty: 'No conversation yet. Try: "How do I create an order?"',
    thinking: 'Thinking…',
    history: 'History',
    delete: 'Delete',
    error: 'The assistant is unavailable. Please retry.',
  },
  fr: {
    title: 'Assistant Carex',
    subtitle: 'Posez-moi des questions sur le système',
    placeholder: 'Tapez votre question…',
    send: 'Envoyer',
    new: 'Nouvelle conversation',
    empty: 'Pas encore de conversation. Essayez : « Comment créer une commande ? »',
    thinking: 'Réflexion…',
    history: 'Historique',
    delete: 'Supprimer',
    error: 'L\'assistant est indisponible. Réessayez.',
  },
  ar: {
    title: 'مساعد Carex',
    subtitle: 'اسألني عن النظام',
    placeholder: 'اكتب سؤالك…',
    send: 'إرسال',
    new: 'محادثة جديدة',
    empty: 'لا توجد محادثة بعد. جرّب: « كيف أنشئ طلباً؟ »',
    thinking: 'يفكر…',
    history: 'السجل',
    delete: 'حذف',
    error: 'المساعد غير متاح. أعد المحاولة.',
  },
};

const ALLOWED_ROLES = new Set(['admin', 'sales_agent', 'logistics_manager', 'operations', 'accountant']);

const ChatWidget = ({ user }) => {
  const { i18n } = useTranslation();
  const lang = ['en', 'fr', 'ar'].includes(i18n.language) ? i18n.language : 'en';
  const L = LABELS[lang];

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);

  // Persist session id locally so reload keeps the conversation
  useEffect(() => {
    if (!user?.id) return;
    const saved = localStorage.getItem(`carex_chat_session_${user.id}`);
    if (saved) setSessionId(saved);
  }, [user?.id]);

  useEffect(() => {
    if (sessionId && user?.id) {
      localStorage.setItem(`carex_chat_session_${user.id}`, sessionId);
    }
  }, [sessionId, user?.id]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Load history when widget opens with an existing session
  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    getChatHistory(sessionId).then((r) => {
      if (!cancelled) setMessages(r.data || []);
    }).catch(() => { /* new session, no history */ });
    return () => { cancelled = true; };
  }, [open, sessionId]);

  if (!user || !ALLOWED_ROLES.has(user.role)) return null;

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setError('');
    setInput('');
    const optimistic = { role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setLoading(true);
    try {
      const { data } = await sendChatMessage({ message: text, session_id: sessionId });
      if (!sessionId) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: data.reply, created_at: data.created_at }]);
    } catch (e) {
      setError(e?.response?.data?.detail || L.error);
      setMessages((m) => m.slice(0, -1)); // remove optimistic if it really failed
      setInput(text);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setError('');
    if (user?.id) localStorage.removeItem(`carex_chat_session_${user.id}`);
  };

  const openHistory = async () => {
    setShowHistory(true);
    try {
      const { data } = await listChatSessions();
      setSessions(data || []);
    } catch {
      setSessions([]);
    }
  };

  const loadSession = async (sid) => {
    setSessionId(sid);
    setShowHistory(false);
    try {
      const { data } = await getChatHistory(sid);
      setMessages(data || []);
    } catch {
      setMessages([]);
    }
  };

  const handleDeleteSession = async (sid, e) => {
    e.stopPropagation();
    await deleteChatSession(sid).catch(() => {});
    setSessions((s) => s.filter((x) => x.session_id !== sid));
    if (sid === sessionId) handleNewChat();
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-launcher"
          aria-label={L.title}
          className="fixed bottom-5 right-5 z-[80] w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
        >
          <ChatCircleDots weight="fill" className="w-7 h-7" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-5 right-5 z-[80] w-[92vw] max-w-[400px] h-[70vh] max-h-[600px] bg-white rounded-xl shadow-2xl border border-border flex flex-col overflow-hidden"
          dir={lang === 'ar' ? 'rtl' : 'ltr'}
        >
          {/* Header */}
          <div className="px-4 py-3 bg-primary text-primary-foreground flex items-center justify-between">
            <div>
              <div className="font-semibold text-sm leading-tight">{L.title}</div>
              <div className="text-[11px] opacity-80 leading-tight">{L.subtitle}</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={openHistory}
                title={L.history}
                className="p-1.5 rounded hover:bg-white/15"
                data-testid="chat-history-btn"
              >
                <ClockCounterClockwise weight="bold" className="w-4 h-4" />
              </button>
              <button
                onClick={handleNewChat}
                title={L.new}
                className="p-1.5 rounded hover:bg-white/15"
                data-testid="chat-new-btn"
              >
                <Plus weight="bold" className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                title="Close"
                className="p-1.5 rounded hover:bg-white/15"
                data-testid="chat-close-btn"
              >
                <X weight="bold" className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* History sub-panel */}
          {showHistory && (
            <div className="flex-1 overflow-y-auto p-2 bg-secondary/30">
              {sessions.length === 0 && (
                <div className="text-center text-xs text-muted-foreground p-6">—</div>
              )}
              {sessions.map((s) => (
                <button
                  key={s.session_id}
                  onClick={() => loadSession(s.session_id)}
                  className={`w-full text-left p-2.5 mb-1.5 rounded-md bg-white border border-border hover:border-primary text-xs flex items-start gap-2 ${s.session_id === sessionId ? 'border-primary' : ''}`}
                  data-testid="chat-session-item"
                >
                  <span className="flex-1 truncate font-medium">{s.title || '—'}</span>
                  <span
                    onClick={(e) => handleDeleteSession(s.session_id, e)}
                    className="text-muted-foreground hover:text-destructive p-0.5"
                  >
                    <Trash className="w-3.5 h-3.5" />
                  </span>
                </button>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => setShowHistory(false)}
                data-testid="chat-history-back"
              >
                ← Back
              </Button>
            </div>
          )}

          {/* Messages */}
          {!showHistory && (
            <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-secondary/20" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-center text-xs text-muted-foreground mt-6 px-4">{L.empty}</div>
              )}
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                      : 'bg-white border border-border rounded-bl-sm'
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-border px-3 py-2 rounded-lg text-sm text-muted-foreground italic">
                    {L.thinking}
                  </div>
                </div>
              )}
              {error && (
                <div className="text-xs text-destructive bg-destructive/10 border border-destructive/20 p-2 rounded">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Composer */}
          {!showHistory && (
            <div className="p-2 border-t border-border bg-white flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
                placeholder={L.placeholder}
                rows={1}
                className="flex-1 resize-none min-h-[40px] max-h-[100px] text-sm"
                data-testid="chat-input"
                disabled={loading}
              />
              <Button
                onClick={handleSend}
                disabled={loading || !input.trim()}
                size="sm"
                className="h-10 px-3"
                data-testid="chat-send-btn"
              >
                <PaperPlaneRight weight="fill" className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
